import { useState, useCallback, useRef } from "react";
import api from "@/utils/axios-utils";
import {
  playNamePronunciationAudio,
  stopNamePronunciationAudio,
} from "@/utils/namePronunciationAudio";
import { type AudioVoice } from "@/constants/audioVoice";

// Module-level caches persist for the full session lifetime — survive modal close/reopen.
// audioCache: full render keyed by name + voice IDs + lang (+ phonemes suffix for play-current)
const audioCache = new Map<
  string,
  { phonemes: string; audioB64: string; sampleRate: number }
>();
// phonemesCache: voice-agnostic, keyed by name:lang — skips LLM re-generation on voice change
const phonemesCache = new Map<string, string>();

/** Clear all pronunciation caches — call on logout so one user's data doesn't bleed into the next. */
export function clearNamePronunciationCaches(): void {
  audioCache.clear();
  phonemesCache.clear();
}

const AUDIO_CACHE_MAX = 50; // ~5 MB upper bound (each entry ≈ 50–100 KB of base64 audio)
const PHONEMES_CACHE_MAX = 100;

/** FIFO eviction: drops the oldest entry when the map is at capacity. */
function setWithSizeLimit<K, V>(
  map: Map<K, V>,
  key: K,
  value: V,
  maxSize: number,
): void {
  if (!map.has(key) && map.size >= maxSize) {
    map.delete(map.keys().next().value as K);
  }
  map.set(key, value);
}

export interface SavedNamePronunciationEntry {
  surfaceText: string;
  phonemes: string;
  provider: string;
}

/** Shape of GET /name-pronunciation/entries values — mirrors backend.apps.web.api.v1.name_pronunciation.NamePronunciationEntryResponse */
export interface NamePronunciationEntryResponse {
  provider: string;
  providerVoiceId: string;
  lang: string;
  surfaceText: string;
  phonemes: string;
  updatedAtMs: number;
}

// Pronunciation is Cartesia-only (the backend sends voice_id straight to
// Cartesia), so non-cartesia voices resolve to undefined.
function namePronunciationVoiceId(voice: AudioVoice): string | undefined {
  return voice.ttsProvider === "cartesia" ? voice.providerVoiceId : undefined;
}

function makeAudioKey(
  prefix: string,
  name: string,
  voice: AudioVoice,
  lang: string,
  suffix = "",
): string {
  return `${prefix}:${name}:${namePronunciationVoiceId(voice)}:${lang}${suffix ? `:${suffix}` : ""}`;
}

export function useNamePronunciationAudio() {
  const [isSpeakingBack, setIsSpeakingBack] = useState(false);
  const [isPlayingCurrent, setIsPlayingCurrent] = useState(false);
  const [lastPhonemes, setLastPhonemes] = useState<string | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);

  // Set to true by stop() so in-flight API calls don't start playback after cancellation.
  const cancelledRef = useRef(false);

  const clearLastPhonemes = useCallback(() => setLastPhonemes(null), []);

  const speakBack = useCallback(
    async (name: string, voice: AudioVoice, lang: string): Promise<void> => {
      if (isSpeakingBack || !name) return;

      const cacheKey = makeAudioKey("speak", name, voice, lang);
      const cached = audioCache.get(cacheKey);
      if (cached) {
        setLastPhonemes(cached.phonemes);
        cancelledRef.current = false;
        setIsSpeakingBack(true);
        setAudioError(null);
        try {
          await playNamePronunciationAudio(cached.audioB64, cached.sampleRate);
        } catch (err) {
          console.error(
            "[useNamePronunciationAudio] Cached speak-back failed:",
            err,
          );
          setAudioError("Unable to play pronunciation preview right now.");
        } finally {
          setIsSpeakingBack(false);
        }
        return;
      }

      cancelledRef.current = false;
      setIsSpeakingBack(true);
      setAudioError(null);
      try {
        const phonemeKey = `${name}:${lang}`;
        const cachedPhonemes = phonemesCache.get(phonemeKey);
        const res = await api.post("/name-pronunciation/generate", {
          name,
          voice_id: namePronunciationVoiceId(voice),
          provider: voice.ttsProvider,
          provider_voice_id: voice.providerVoiceId,
          language: lang,
          include_greeting: true,
          ...(cachedPhonemes && { phonemes: cachedPhonemes }),
        });
        if (cancelledRef.current) return;
        const phonemes: string = res.data.phonemes;
        setLastPhonemes(phonemes);
        if (phonemes)
          setWithSizeLimit(
            phonemesCache,
            phonemeKey,
            phonemes,
            PHONEMES_CACHE_MAX,
          );
        if (res.data.audio_b64) {
          setWithSizeLimit(
            audioCache,
            cacheKey,
            {
              phonemes,
              audioB64: res.data.audio_b64,
              sampleRate: res.data.sample_rate,
            },
            AUDIO_CACHE_MAX,
          );
          await playNamePronunciationAudio(
            res.data.audio_b64,
            res.data.sample_rate,
          );
        }
      } catch (err) {
        console.error("[useNamePronunciationAudio] speakBack failed:", err);
        setAudioError("Unable to play pronunciation preview right now.");
      } finally {
        setIsSpeakingBack(false);
      }
    },
    [isSpeakingBack],
  );

  const playCurrent = useCallback(
    async (
      name: string,
      voice: AudioVoice,
      lang: string,
      savedEntry?: SavedNamePronunciationEntry,
    ): Promise<void> => {
      if (isPlayingCurrent || !name) return;

      const hasSavedPhonemes = Boolean(savedEntry?.phonemes);
      const savedPhonemes = hasSavedPhonemes ? savedEntry!.phonemes : "";
      const cacheKey = makeAudioKey("play", name, voice, lang, savedPhonemes);
      const cached = audioCache.get(cacheKey);
      if (cached) {
        cancelledRef.current = false;
        setIsPlayingCurrent(true);
        setAudioError(null);
        try {
          await playNamePronunciationAudio(cached.audioB64, cached.sampleRate);
        } catch (err) {
          console.error(
            "[useNamePronunciationAudio] Cached play-current failed:",
            err,
          );
          setAudioError("Unable to play pronunciation preview right now.");
        } finally {
          setIsPlayingCurrent(false);
        }
        return;
      }

      cancelledRef.current = false;
      setIsPlayingCurrent(true);
      setAudioError(null);
      try {
        const payload = {
          name,
          voice_id: namePronunciationVoiceId(voice),
          provider: voice.ttsProvider,
          provider_voice_id: voice.providerVoiceId,
          language: lang,
          include_greeting: true,
          ...(hasSavedPhonemes
            ? { phonemes: savedEntry!.phonemes }
            : { skip_llm: true }),
        };
        const res = await api.post("/name-pronunciation/generate", payload);
        if (cancelledRef.current) return;
        if (res.data.audio_b64) {
          setWithSizeLimit(
            audioCache,
            cacheKey,
            {
              phonemes: res.data.phonemes ?? "",
              audioB64: res.data.audio_b64,
              sampleRate: res.data.sample_rate,
            },
            AUDIO_CACHE_MAX,
          );
          await playNamePronunciationAudio(
            res.data.audio_b64,
            res.data.sample_rate,
          );
        }
      } catch (err) {
        console.error("[useNamePronunciationAudio] playCurrent failed:", err);
        setAudioError("Unable to play pronunciation preview right now.");
      } finally {
        setIsPlayingCurrent(false);
      }
    },
    [isPlayingCurrent],
  );

  /** Resolves phonemes for a name+lang, using the phonemes cache before hitting the API. */
  const resolvePhonemes = useCallback(
    async (
      name: string,
      voice: AudioVoice,
      lang: string,
    ): Promise<string | null> => {
      const phonemeKey = `${name}:${lang}`;
      const cached = phonemesCache.get(phonemeKey);
      if (cached) return cached;
      try {
        const res = await api.post<{ phonemes: string }>(
          "/name-pronunciation/generate",
          {
            name,
            voice_id: namePronunciationVoiceId(voice),
            language: lang,
          },
        );
        const phonemes = res.data.phonemes ?? null;
        if (phonemes)
          setWithSizeLimit(
            phonemesCache,
            phonemeKey,
            phonemes,
            PHONEMES_CACHE_MAX,
          );
        return phonemes;
      } catch (err) {
        console.error(
          "[useNamePronunciationAudio] resolvePhonemes failed:",
          err,
        );
        return null;
      }
    },
    [],
  );

  return {
    isSpeakingBack,
    isPlayingCurrent,
    lastPhonemes,
    audioError,
    setAudioError,
    clearLastPhonemes,
    speakBack,
    playCurrent,
    resolvePhonemes,
    stop: () => {
      cancelledRef.current = true;
      stopNamePronunciationAudio();
      setIsSpeakingBack(false);
      setIsPlayingCurrent(false);
    },
  };
}
